import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { Button, InputGroup, Modal, Radio, RadioButton, toastManager } from '@systemeio/components'
import { useTranslation } from 'next-i18next'
import { AccessDenied } from 'shared/errors/access-denied'
import { ConflictError } from 'shared/errors/conflict-error'
import { NotFound } from 'shared/errors/not-found'
import { UnprocessableContentError } from 'shared/errors/unprocessable-content-error'
import {
  REPORT_EXPLANATION_MAX_LENGTH,
  REPORT_REASON_VALUES,
} from 'modules/community/const/report-reasons'
import type { ReportReason, ReportRequest } from 'modules/community/types/moderation'

export type ReportContentTargetType = 'post' | 'comment'

interface ReportContentModalProps {
  opened: boolean
  onClose: () => void
  targetType: ReportContentTargetType
  isSubmitting: boolean
  onSubmit: (body: ReportRequest) => Promise<void>
}

export const ReportContentModal = ({
  opened,
  onClose,
  targetType,
  isSubmitting,
  onSubmit,
}: ReportContentModalProps): ReactNode => {
  const { t } = useTranslation()
  const [reason, setReason] = useState<ReportReason | ''>('')
  const [explanation, setExplanation] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!opened) return
    setReason('')
    setExplanation('')
    setError(null)
  }, [opened])

  const resetAndClose = (): void => {
    if (isSubmitting) return
    onClose()
  }

  const handleSubmit = async (): Promise<void> => {
    setError(null)

    if (!reason) return

    const explanationTrimmed = explanation.trim()
    if (reason === 'other' && !explanationTrimmed) {
      setError(t('community.report.modal.explanation_required'))
      return
    }

    try {
      await onSubmit({
        reason,
        explanation: explanationTrimmed || null,
      })
      toastManager.add({ type: 'success', title: t('community.report.success') })
      onClose()
    } catch (error_) {
      if (error_ instanceof ConflictError) {
        toastManager.add({ type: 'error', title: t('community.report.modal.already_reported') })
        return
      }
      if (error_ instanceof AccessDenied) {
        toastManager.add({ type: 'error', title: t('community.report.modal.forbidden') })
        return
      }
      if (error_ instanceof NotFound) {
        toastManager.add({ type: 'error', title: t('community.report.modal.not_found') })
        return
      }
      toastManager.add({
        type: 'error',
        title:
          error_ instanceof UnprocessableContentError
            ? t('community.report.modal.validation_error')
            : t('core.error.internal_error_message'),
      })
    }
  }

  if (!opened) {
    return null
  }

  return (
    <Modal.Root
      open
      onOpenChange={value => {
        if (!value) resetAndClose()
      }}
    >
      <Modal.Popup>
        <Modal.Header>
          <div className="flex flex-col gap-1">
            <Modal.Title>
              {targetType === 'post'
                ? t('community.report.modal.title_post')
                : t('community.report.modal.title_comment')}
            </Modal.Title>
            <Modal.Subtitle>
              {targetType === 'post'
                ? t('community.report.modal.description_post')
                : t('community.report.modal.description_comment')}
            </Modal.Subtitle>
          </div>
        </Modal.Header>
        <div className="flex flex-col gap-6 px-8 py-6">
          <Radio.Group
            className="gap-2"
            value={reason}
            disabled={isSubmitting}
            onValueChange={value => {
              setReason(value as ReportReason | '')
              setError(null)
            }}
          >
            {REPORT_REASON_VALUES.map(value => (
              <RadioButton
                key={value}
                value={value}
                label={t(`community.report.reasons.${value}`)}
              />
            ))}
          </Radio.Group>

          <div className="flex flex-col gap-1">
            <InputGroup.Root>
              <InputGroup.Input
                placeholder={t('comments.components.comments.input_placeholder_label')}
                value={explanation}
                maxLength={REPORT_EXPLANATION_MAX_LENGTH}
                disabled={isSubmitting}
                onChange={event => {
                  setExplanation(event.target.value)
                  setError(null)
                }}
              />
            </InputGroup.Root>
            {error && <p className="text-sm text-text-error">{error}</p>}
          </div>

          <Button
            variant="primary"
            type="button"
            className="w-full"
            disabled={!reason || isSubmitting}
            isLoading={isSubmitting}
            onClick={() => {
              void handleSubmit()
            }}
          >
            {t('comments.components.comments.send_label')}
          </Button>
        </div>
      </Modal.Popup>
    </Modal.Root>
  )
}
